import {
  RiAddLine,
  RiArrowLeftRightLine,
  RiArrowUpDownLine,
  RiInboxArchiveLine,
  RiInboxUnarchiveLine,
  RiLeafLine,
  RiMoneyDollarBoxLine,
  RiStackLine,
} from '@remixicon/react';
import { useNavigate } from '@tanstack/react-router';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { AgDropdownItem, AgDropdownMenu, AgDropdownSeparator } from 'src/components/ui';
import { useCreateBill } from 'src/modules/accounting/bills/edit-bill/bill.api';
import { useCreateInventoryTransfer } from 'src/modules/entities/inventoryTransfers';
import { useCreateProductionRun } from 'src/modules/entities/productionRuns';
import { appLinks } from 'src/routing/appLinks';
import { useCreateGrowerProduct } from '../orders/growerProducts/api/create';
import { useCreatePurchaseOrder } from '../orders/purchaseOrders/api/creates';
import { useCreateBuySellOrder, useCreateSalesOrder } from '../orders/salesOrders/list/api/creates';

export default function CreateNewButton() {
  const flags = useFlags();
  const navigate = useNavigate();
  const createPurchaseOrder = useCreatePurchaseOrder();
  const createSalesOrder = useCreateSalesOrder();
  const createBuySellOrder = useCreateBuySellOrder();
  const createGrowerProduct = useCreateGrowerProduct();
  const createBill = useCreateBill();
  const { create: createProductionRun } = useCreateProductionRun({ origin: 'createNewMenu' });
  const { create: createInventoryTransfer } = useCreateInventoryTransfer({ origin: 'createNewMenu' });

  function handleCreateNewSalesOrder() {
    createSalesOrder();
  }

  function handleCreateNewPurchaseOrder() {
    createPurchaseOrder();
  }

  function handleCreateNewBuySellOrder() {
    createBuySellOrder();
  }

  function handleCreateNewGrowerProduct() {
    createGrowerProduct();
  }

  function handleCreateInvoice() {
    createBill('INVOICE')
      .then((bill) => navigate(appLinks.bill({ slug: bill.slug })))
      .catch(() => {});
  }

  function handleCreatePayable() {
    createBill('PAYABLE')
      .then((bill) => navigate(appLinks.bill({ slug: bill.slug })))
      .catch(() => {});
  }

  function handleCreateProductionRun() {
    createProductionRun(
      {},
      {
        onCompleted: ({ createProductionRun }) => {
          void navigate(appLinks.productionRun({ slug: createProductionRun.slug }));
        },
      }
    );
  }

  function handleCreateInventoryTransfer() {
    createInventoryTransfer(
      {},
      {
        onCompleted: ({ createInventoryTransfer }) => {
          void navigate(appLinks.inventoryTransfer({ slug: createInventoryTransfer.slug }));
        },
      }
    );
  }

  const hasOrders =
    flags.permissionsCanCreateSalesOrders ||
    flags.permissionsCanCreatePurchaseOrders ||
    flags.buySellOrders ||
    flags.workOrders;
  const hasAccounting = flags.accounting;
  const hasInventory = flags.productionRuns || flags.inventoryTransfers;

  if (!hasOrders && !hasAccounting && !hasInventory) {
    return null;
  }

  return (
    <AgDropdownMenu
      variant="white"
      size="sm"
      icon={<RiAddLine size={20} />}
      data-testid="header-create-new-btn"
      items={
        <>
          {flags.permissionsCanCreateSalesOrders && (
            <AgDropdownItem icon={<RiInboxUnarchiveLine size={18} />} onAction={handleCreateNewSalesOrder}>
              Sales Order
            </AgDropdownItem>
          )}
          {flags.permissionsCanCreatePurchaseOrders && (
            <AgDropdownItem
              icon={<RiInboxArchiveLine size={18} />}
              onAction={handleCreateNewPurchaseOrder}
              data-testid="header-create-purchase-order"
            >
              Purchase Order
            </AgDropdownItem>
          )}
          {flags.buySellOrders && (
            <AgDropdownItem icon={<RiArrowUpDownLine size={18} />} onAction={handleCreateNewBuySellOrder}>
              Buy-Sell Order
            </AgDropdownItem>
          )}
          {flags.workOrders && (
            <AgDropdownItem icon={<RiLeafLine size={18} />} onAction={handleCreateNewGrowerProduct}>
              Grower Product
            </AgDropdownItem>
          )}

          {hasOrders && hasAccounting && <AgDropdownSeparator />}

          {hasAccounting && (
            <>
              <AgDropdownItem icon={<RiMoneyDollarBoxLine size={18} />} onAction={handleCreateInvoice}>
                Invoice
              </AgDropdownItem>
              <AgDropdownItem icon={<RiMoneyDollarBoxLine size={18} />} onAction={handleCreatePayable}>
                Payable
              </AgDropdownItem>
            </>
          )}

          {(hasOrders || hasAccounting) && hasInventory && <AgDropdownSeparator />}

          {flags.productionRuns && (
            <AgDropdownItem icon={<RiStackLine size={18} />} onAction={handleCreateProductionRun}>
              Production Run
            </AgDropdownItem>
          )}
          {flags.inventoryTransfers && (
            <AgDropdownItem icon={<RiArrowLeftRightLine size={18} />} onAction={handleCreateInventoryTransfer}>
              Inventory Transfer
            </AgDropdownItem>
          )}
        </>
      }
    >
      Create new...
    </AgDropdownMenu>
  );
}
